import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Delete, Pencil } from '@factorialco/f0-react/icons/app'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import UninstallApplicationModal from 'modules/software_management/components/ApplicationDetailsContent/UninstallApplicationModal'
import { useDeleteSoftwareSetup } from 'modules/software_management/hooks/useDeleteSoftwareSetup'
import {
  SoftwareSetup,
  getSoftware,
  licenseTypeOptions,
} from 'modules/software_management/lib/software'
import { EditApplicationModal } from 'modules/software_management/pages/EditApplicationModal'

type Props = {
  softwareSetup: SoftwareSetup
}

export const ApplicationHeader = ({ softwareSetup }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const [uninstallModalOpen, setUninstallModalOpen] = useState(false)
  const software = getSoftware(softwareSetup)
  const [editModalOpen, setEditModalOpen] = useState(false)

  const metadata = [
    ...(softwareSetup.renewalDate
      ? [
          {
            label: 'Renewal date',
            value: {
              type: 'date' as const,
              formattedDate: dateBackendToUI(softwareSetup.renewalDate || ''),
            },
          },
        ]
      : []),

    ...(softwareSetup.licenseType
      ? [
          {
            label: 'License type',
            value: {
              type: 'text' as const,
              content:
                licenseTypeOptions().find((option) => option.value === softwareSetup.licenseType)
                  ?.label || '',
            },
          },
        ]
      : []),
    ,
  ]

  const onCloseUninstallModal = () => {
    setUninstallModalOpen(false)
  }

  const onSubmitUninstallModal = () => {
    setUninstallModalOpen(false)
    uninstallSoftware()
  }

  const { delete: deleteSoftwareSetup } = useDeleteSoftwareSetup()
  const { performAction: uninstallSoftware } = useAsyncAction(
    async () => {
      const res = await deleteSoftwareSetup(softwareSetup.id)
      if (res.errors) {
        throw new Error(`Failed to remove software`)
      }

      if (software) {
        track(trackingEvents.softwareManagement.deleteSoftwareSetup, {
          software_id: software.id,
          software_name: software.name,
          software_setup_id: softwareSetup.id,
          with_custom_software: !!softwareSetup?.customSoftware,
        })
      }

      navigate(navigation.software.applications.fullPath())
    },
    {
      successFlash: true,
      errorFlash: true,
      successMessage: i18n.t('software_management.software_setups.form.alert.uninstall_success'),
      errorMessage: i18n.t('software_management.software_setups.form.alert.uninstall_error'),
    }
  )

  return (
    <>
      <ResourceHeader
        title={software.name}
        metadata={metadata}
        avatar={{ type: 'generic', name: software.name, src: software.logoUrl || undefined }}
        primaryAction={{
          label: i18n.t('common.edit'),
          icon: Pencil,
          onClick: () => {
            setEditModalOpen(true)
          },
        }}
        secondaryActions={[
          {
            label: i18n.t('software_management.software_setups.actions.uninstall.title'),
            icon: Delete,
            variant: 'critical',
            onClick: () => {
              setUninstallModalOpen(true)
            },
          },
        ]}
      />
      {editModalOpen && (
        <EditApplicationModal
          softwareSetupId={softwareSetup.id}
          companyId={companyId}
          onClose={() => {
            setEditModalOpen(false)
          }}
        />
      )}
      {uninstallModalOpen && (
        <UninstallApplicationModal
          onClose={onCloseUninstallModal}
          onSubmit={onSubmitUninstallModal}
        />
      )}
    </>
  )
}
